import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { adminOrderService } from '../services'

function buildReceiptItems(shipment) {
  return (shipment?.items || [])
    .filter((si) => Number(si.remaining_to_receive) > 0)
    .map((si) => ({
      order_line_item_id: si.order_line_item_id,
      product_id: si.product_id,
      received_quantity: Number(si.remaining_to_receive) || 0,
      damaged_quantity: 0,
    }))
}

const ConfirmShipmentReceiptButton = ({
  orderId,
  shipment,
  shipmentLabel,
  onSuccess,
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleConfirm = async () => {
    const items = buildReceiptItems(shipment)
    if (!items.length) {
      setError('No remaining units to receive on this shipment.')
      return
    }

    const isLegacy = Boolean(shipment?.legacy)
    const shipmentId = !isLegacy && Number(shipment?.id) > 0 ? shipment.id : undefined

    try {
      setLoading(true)
      setError(null)
      await adminOrderService.confirmOrderReceived(orderId, {
        items,
        shipment_id: shipmentId,
        notes: 'Receipt confirmed by super admin on behalf of retailer',
      })
      setDialogOpen(false)
      onSuccess?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to confirm receipt')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button
        variant="contained"
        color="success"
        size="small"
        startIcon={<CheckCircleIcon />}
        onClick={() => {
          setError(null)
          setDialogOpen(true)
        }}
        disabled={disabled}
        title="Confirm receipt on behalf of the retailer"
        sx={{ mt: 1.5 }}
      >
        Confirm Receipt
      </Button>

      <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: error ? 2 : 0 }}>
            Confirm receipt for {shipmentLabel} on behalf of the retailer? All remaining
            units on this shipment will be marked as received.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="success"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Confirming...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ConfirmShipmentReceiptButton
